<script setup>
import { computed, defineAsyncComponent, nextTick, onBeforeUnmount, ref, watch } from 'vue'
import { RouterLink, useRoute } from 'vue-router'
import { api } from '../api'
import { useI18n } from '../i18n'
import { ChartContractError, shouldFallbackToJpeg, validateChartContract } from '../reports/chartContract'
import { downloadReportExport } from '../reports/exportDownload'
import { reportErrorMessage } from '../reports/reportErrors'
import { buildParameterLayout, buildParameterValues, validateProductRanges } from '../reports/reportParameterRanges'
import ProductRangeField from '../components/reports/ProductRangeField.vue'
import { session } from '../session'
import { activeNaftaPosInstance } from '../platform/context'

const InteractiveChart = defineAsyncComponent(() => import('../components/reports/InteractiveChart.vue'))

const route = useRoute()
const { t } = useI18n()
const activeStation = computed(() => activeNaftaPosInstance(session.platformContext))
const template = ref(null)
const parameterForm = ref(null)
const parameterValues = ref({})
const loading = ref(true)
const reportRunning = ref(false)
const exportRunning = ref('')
const chartData = ref(null)
const chartImageUrl = ref('')
const chartFallbackActive = ref(false)
const chartRequestValues = ref({})
const chartPerformance = ref({ interaction: [], resize: [] })
const error = ref('')
const noData = ref(false)
const visibleParameters = computed(() => parameterForm.value?.parameters.filter((parameter) => !parameter.autoFilled) ?? [])
const parameterLayout = computed(() => buildParameterLayout(visibleParameters.value))
const rangeErrors = ref({})
const rangeRefs = new Map()
const hasProductRange = computed(() => parameterLayout.value.some((item) => item.kind === 'range'))

async function loadParameterForm() {
  const resource = template.value.kind === 'CHART' ? 'charts' : 'reports'
  const result = await api(`/${resource}/${template.value.id}/parameters`)
  parameterForm.value = result.form
  rangeErrors.value = {}
  parameterValues.value = Object.fromEntries(result.form.parameters.map((parameter) => [
    parameter.name,
    parameter.selection === 'Multiple' ? [...parameter.defaultValues] : (parameter.defaultValues[0] ?? ''),
  ]))
}

async function load() {
  loading.value = true
  error.value = ''
  noData.value = false
  parameterForm.value = null
  clearChartOutput()
  try {
    const detail = await api(`/catalog/${route.params.templateId}`)
    template.value = detail.item
    if (activeStation.value) await loadParameterForm()
  } catch (cause) {
    error.value = reportErrorMessage(cause, t, 'parameters')
  } finally {
    loading.value = false
  }
}

function clearChartImage() {
  if (chartImageUrl.value) URL.revokeObjectURL(chartImageUrl.value)
  chartImageUrl.value = ''
}

function clearChartOutput() {
  clearChartImage()
  chartData.value = null
  chartFallbackActive.value = false
  chartRequestValues.value = {}
  chartPerformance.value = { interaction: [], resize: [] }
}

function valuesForExport() {
  return buildParameterValues(parameterForm.value?.parameters ?? [], parameterValues.value)
}

function validatedValuesForExport() {
  const validation = validateProductRanges(visibleParameters.value, parameterValues.value)
  rangeErrors.value = validation.errors
  if (validation.valid) return valuesForExport()
  nextTick(() => {
    const item = parameterLayout.value.find((candidate) => candidate.kind === 'range'
      && (candidate.start.name === validation.firstInvalidName || candidate.end.name === validation.firstInvalidName))
    if (item) rangeRefs.get(item.group)?.focus(validation.firstInvalidName)
  })
  return null
}

function updateRangeValue({ group, name, value }) {
  parameterValues.value[name] = value
  if (!rangeErrors.value[group]) return
  const validation = validateProductRanges(visibleParameters.value, parameterValues.value)
  rangeErrors.value = validation.errors
}

function setRangeRef(group, element) {
  if (element) rangeRefs.set(group, element)
  else rangeRefs.delete(group)
}

function rangeErrorMessage(group) {
  const code = rangeErrors.value[group]?.code
  if (code === 'required') return t('productRangeRequired')
  if (code === 'integer') return t('productRangeInteger')
  if (code === 'order') return t('productRangeOrder')
  return ''
}

function snapshotValues(values) {
  return Object.fromEntries(Object.entries(values).map(([name, value]) => [
    name, Array.isArray(value) ? [...value] : value,
  ]))
}

function selectedOptions(parameter) {
  const selectedValues = Array.isArray(parameterValues.value[parameter.name]) ? parameterValues.value[parameter.name] : []
  return parameter.options.filter((option) => selectedValues.includes(option.value))
}

function multipleSummary(parameter) {
  const selected = selectedOptions(parameter)
  if (!selected.length) return t('chooseMultiple')
  return selected.length === 1 ? selected[0].label : t('selectedCount', { count: selected.length })
}

function allSelected(parameter) {
  return parameter.options.length > 0 && selectedOptions(parameter).length === parameter.options.length
}

function toggleAll(parameter) {
  parameterValues.value[parameter.name] = allSelected(parameter) ? [] : parameter.options.map((option) => option.value)
}

function temporalInputType(parameter) {
  if (parameter.valueType === 'java.sql.Timestamp') return 'datetime-local'
  if (parameter.valueType === 'java.sql.Date') return 'date'
  if (parameter.valueType === 'java.sql.Time') return 'time'
  return ''
}

function temporalInputValue(parameter) {
  const value = parameterValues.value[parameter.name] ?? ''
  return parameter.valueType === 'java.sql.Timestamp' ? value.replace(' ', 'T') : value
}

function updateTemporalParameter(parameter, rawValue) {
  if (parameter.valueType !== 'java.sql.Timestamp') {
    parameterValues.value[parameter.name] = rawValue
    return
  }
  const value = rawValue.replace('T', ' ')
  parameterValues.value[parameter.name] = /^\d{4}-\d{2}-\d{2} \d{2}:\d{2}$/.test(value) ? `${value}:00` : value
}

function pdfFileName() {
  const title = (template.value?.displayName ?? 'report')
    .replace(/[\\/:*?"<>|\u0000-\u001F]/g, '_')
    .trim()
    .slice(0, 120) || 'report'
  return `${title}.pdf`
}

function pdfAction() {
  const resource = template.value.kind === 'CHART' ? 'charts' : 'reports'
  return `/api/${resource}/${template.value.id}/pdf/${encodeURIComponent(pdfFileName())}?ui=1`
}

async function openPdf() {
  if (!template.value) return
  const submittedValues = validatedValuesForExport()
  if (!submittedValues) return
  error.value = ''
  noData.value = false
  const targetName = `next_reports_pdf_${Date.now()}`
  const reportWindow = window.open('about:blank', targetName)
  if (!reportWindow) {
    error.value = t('popupBlocked')
    return
  }
  reportRunning.value = true
  const removeNoDataListener = () => window.removeEventListener('message', onExportMessage)
  const onExportMessage = (event) => {
    if (event.origin !== window.location.origin || event.data?.type !== 'next-reports-export-no-data') return
    noData.value = true
    removeNoDataListener()
  }
  window.addEventListener('message', onExportMessage)
  try {
    const form = document.createElement('form')
    const values = document.createElement('input')
    form.method = 'POST'
    form.action = pdfAction()
    form.target = targetName
    form.acceptCharset = 'UTF-8'
    form.style.display = 'none'
    values.name = 'values'
    values.value = JSON.stringify(submittedValues)
    form.append(values)
    document.body.append(form)
    form.submit()
    form.remove()
    window.setTimeout(removeNoDataListener, 60 * 1000)
  } catch (cause) {
    removeNoDataListener()
    reportWindow.close()
    error.value = reportErrorMessage(cause, t, template.value.kind === 'CHART' ? 'chartPdf' : 'pdf')
  } finally {
    reportRunning.value = false
  }
}

async function downloadXlsx() {
  if (!template.value || template.value.kind !== 'REPORT') return
  const values = validatedValuesForExport()
  if (!values) return
  error.value = ''
  noData.value = false
  exportRunning.value = 'xlsx'
  try {
    await downloadReportExport({
      path: `/reports/${template.value.id}/xlsx`,
      values,
      mediaType: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      extension: '.xlsx',
      fallbackName: template.value.displayName,
    })
  } catch (cause) {
    if (cause?.code === 'no_data') noData.value = true
    else error.value = reportErrorMessage(cause, t, 'xlsx')
  } finally {
    exportRunning.value = ''
  }
}

async function downloadHtml() {
  if (!template.value || template.value.kind !== 'REPORT') return
  const values = validatedValuesForExport()
  if (!values) return
  error.value = ''
  noData.value = false
  exportRunning.value = 'html'
  try {
    await downloadReportExport({
      path: `/reports/${template.value.id}/html`,
      values,
      mediaType: 'text/html',
      extension: '.html',
      fallbackName: template.value.displayName,
    })
  } catch (cause) {
    if (cause?.code === 'no_data') noData.value = true
    else error.value = reportErrorMessage(cause, t, 'html')
  } finally {
    exportRunning.value = ''
  }
}

async function renderChart() {
  if (!template.value || template.value.kind !== 'CHART') return
  const values = validatedValuesForExport()
  if (!values) return
  error.value = ''
  noData.value = false
  clearChartOutput()
  reportRunning.value = true
  chartRequestValues.value = snapshotValues(values)
  try {
    const response = await api(`/charts/${template.value.id}/data`, {
      method: 'POST', body: JSON.stringify({ values }),
    })
    chartData.value = validateChartContract(response)
  } catch (cause) {
    if (shouldFallbackToJpeg(cause)) await requestChartJpeg(values, true)
    else if (cause?.code === 'no_data') noData.value = true
    else error.value = cause instanceof ChartContractError ? t('chartContractFailed') : reportErrorMessage(cause, t, 'chart')
  } finally {
    reportRunning.value = false
  }
}

async function renderChartJpeg() {
  if (!template.value || template.value.kind !== 'CHART') return
  const values = validatedValuesForExport()
  if (!values) return
  error.value = ''
  noData.value = false
  reportRunning.value = true
  clearChartOutput()
  chartRequestValues.value = snapshotValues(values)
  try {
    await requestChartJpeg(values, false)
  } finally {
    reportRunning.value = false
  }
}

async function requestChartJpeg(values, automatic) {
  try {
    const response = await fetch(`/api/charts/${template.value.id}/image`, {
      method: 'POST', credentials: 'include', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ values }),
    })
    if (!response.ok) {
      const payload = await response.json().catch(() => null)
      const failure = new Error(payload?.error?.message ?? t('chartFailed'))
      failure.code = payload?.error?.code
      failure.status = response.status
      throw failure
    }
    const imageUrl = URL.createObjectURL(await response.blob())
    clearChartImage()
    chartData.value = null
    chartImageUrl.value = imageUrl
    chartFallbackActive.value = automatic
  } catch (cause) {
    chartFallbackActive.value = false
    if (cause?.code === 'no_data') noData.value = true
    else error.value = reportErrorMessage(cause, t, 'chart')
  }
}

async function handleChartRenderError() {
  if (!chartData.value || chartFallbackActive.value || reportRunning.value) return
  reportRunning.value = true
  error.value = ''
  try {
    await requestChartJpeg(snapshotValues(chartRequestValues.value), true)
  } finally {
    reportRunning.value = false
  }
}

function recordChartPerformance(measurement) {
  const bucket = chartPerformance.value[measurement.kind]
  if (!bucket) return
  bucket.push(measurement.durationMs)
  if (bucket.length > 20) bucket.shift()
}

watch(() => route.params.templateId, load, { immediate: true })
watch(parameterValues, () => {
  noData.value = false
  if (chartData.value || chartImageUrl.value) clearChartOutput()
}, { deep: true })
onBeforeUnmount(clearChartOutput)
</script>

<template>
  <section class="report-page">
    <RouterLink class="back-link" :to="{ name: 'home' }">{{ t('backToCatalog') }}</RouterLink>
    <p v-if="error" class="notice error" role="alert">{{ error }}</p>
    <div v-if="loading" class="empty">{{ t('templateLoading') }}</div>
    <template v-else-if="template">
      <div class="page-title report-title">
        <div>
          <p class="eyebrow">{{ template.kind === 'CHART' ? t('chart') : t('report') }}</p>
          <h1>{{ template.displayName }}</h1>
        </div>
        <span v-if="activeStation" class="status success">{{ t('stationBadge', { name: activeStation.displayName }) }}</span>
      </div>

      <div v-if="!activeStation" class="card empty">{{ t('chooseStationForTemplate') }}</div>
      <section v-else class="card report-parameters">
        <p class="eyebrow">{{ template.kind === 'CHART' ? t('chartParameters') : t('reportParameters') }}</p>
        <h2>{{ t('filters') }}</h2>
        <div v-if="parameterForm" class="parameter-form">
          <template v-for="item in parameterLayout" :key="item.kind === 'range' ? `range-${item.group}` : item.parameter.name">
            <ProductRangeField
              v-if="item.kind === 'range'"
              :ref="(element) => setRangeRef(item.group, element)"
              :item="item"
              :values="parameterValues"
              :error="rangeErrorMessage(item.group)"
              :labels="{ title: t('productRangeTitle'), start: t('productRangeStart'), end: t('productRangeEnd'), hint: t('productRangeHint') }"
              @update="updateRangeValue"
            />
          <div v-else class="parameter-field" :class="{ 'is-multiple': item.parameter.selection === 'Multiple' }">
            <span>{{ item.parameter.label }}<em v-if="item.parameter.mandatory"> *</em></span>
            <details v-if="item.parameter.options.length && item.parameter.selection === 'Multiple'" class="multi-select">
              <summary>
                <span class="multi-select-summary">{{ multipleSummary(item.parameter) }}</span>
                <span class="multi-select-count">{{ selectedOptions(item.parameter).length }}/{{ item.parameter.options.length }}</span>
              </summary>
              <div class="multi-select-panel">
                <button type="button" class="multi-select-toggle" @click="toggleAll(item.parameter)">{{ allSelected(item.parameter) ? t('clear') : t('selectAll') }}</button>
                <div class="multi-select-options">
                  <label v-for="option in item.parameter.options" :key="option.value" class="multi-select-option">
                    <input v-model="parameterValues[item.parameter.name]" type="checkbox" :value="option.value">
                    <span>{{ option.label }}</span>
                  </label>
                </div>
              </div>
            </details>
            <select v-else-if="item.parameter.options.length" v-model="parameterValues[item.parameter.name]" :aria-label="item.parameter.label">
              <option disabled value="">{{ t('selectValue') }}</option>
              <option v-for="option in item.parameter.options" :key="option.value" :value="option.value">{{ option.label }}</option>
            </select>
            <input v-else-if="temporalInputType(item.parameter)" :value="temporalInputValue(item.parameter)" :type="temporalInputType(item.parameter)" :step="item.parameter.valueType === 'java.sql.Timestamp' || item.parameter.valueType === 'java.sql.Time' ? 1 : undefined" :aria-label="item.parameter.label" @input="updateTemporalParameter(item.parameter, $event.target.value)" />
            <input v-else v-model.trim="parameterValues[item.parameter.name]" type="text" :aria-label="item.parameter.label" :placeholder="item.parameter.valueType" />
          </div>
          </template>
        </div>
        <section v-if="noData" class="notice no-data-notice" role="alert" aria-live="polite">
          <strong>{{ t('noDataTitle') }}</strong>
          <span>{{ hasProductRange ? t('productRangeNoData') : t('noDataHint') }}</span>
        </section>
        <div v-if="template.kind === 'REPORT'" class="report-actions">
          <button class="button primary" type="button" :disabled="reportRunning || exportRunning || !parameterForm" @click="openPdf">
            {{ reportRunning ? t('generatingPdf') : t('openPdf') }}
          </button>
          <button class="button button-secondary" type="button" :disabled="reportRunning || exportRunning || !parameterForm" @click="downloadXlsx">
            {{ exportRunning === 'xlsx' ? t('downloadingXlsx') : t('downloadXlsx') }}
          </button>
          <button class="button button-secondary" type="button" :disabled="reportRunning || exportRunning || !parameterForm" @click="downloadHtml">
            {{ exportRunning === 'html' ? t('downloadingHtml') : t('downloadHtml') }}
          </button>
        </div>
        <div v-else class="chart-actions">
          <button class="button button-secondary" type="button" :disabled="reportRunning || !parameterForm" @click="renderChart">
            {{ reportRunning ? t('buildingChart') : t('buildChart') }}
          </button>
          <button class="button button-secondary" type="button" :disabled="reportRunning || !parameterForm" @click="renderChartJpeg">
            {{ reportRunning ? t('buildingChartJpeg') : t('buildChartJpeg') }}
          </button>
          <button class="button primary" type="button" :disabled="reportRunning || !parameterForm" @click="openPdf">
            {{ reportRunning ? t('generatingChartPdf') : t('openChartPdf') }}
          </button>
        </div>
        <p v-if="chartFallbackActive" class="notice chart-fallback-notice" role="status" aria-live="polite">
          {{ t('chartFallbackNotice') }}
        </p>
        <div v-if="template.kind === 'CHART' && chartData" class="chart-preview chart-preview-interactive">
          <InteractiveChart
            :contract="chartData"
            :aria-label="t('chartInteractiveAria', { name: template.displayName })"
            :summary="t('chartInteractiveSummary', { name: template.displayName, series: chartData.series.length, categories: chartData.categories.length })"
            @render-error="handleChartRenderError"
            @performance-measure="recordChartPerformance"
          />
        </div>
        <div v-if="template.kind === 'CHART' && chartImageUrl" class="chart-preview">
          <img :src="chartImageUrl" :alt="t('chartAlt', { name: template.displayName })">
        </div>
      </section>
    </template>
  </section>
</template>
